import React, { useState } from 'react';
import {
  Typography,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Select,
  MenuItem,
  InputBase,
  IconButton,
  Paper,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { styled } from '@mui/material/styles';
import { getUniqueComponentCounts } from './utils';
import { useTranslation } from 'react-i18next';
import { CLIENT_THEME, clientThemePalette } from 'utils/clientTheme';

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'rgba(255, 255, 255, 0.7)',
  marginLeft: '10px',
  flex: 1,
  '& .MuiInputBase-input': {
    fontSize: 16,
    '&::placeholder': {
      color: 'rgba(255, 255, 255, 0.5)',
      opacity: 1,
    },
  },
}));

const SearchButton = styled(IconButton)(({ theme }) => ({
  padding: 10,
  color: 'rgba(255, 255, 255, 0.7)',
}));

const SearchContainer = styled(Paper)(({ theme }) => ({
  backgroundColor: 'rgba(30, 41, 59, 0.8)',
  borderRadius: 8,
  display: 'flex',
  alignItems: 'center',
  border: '1px solid rgba(255, 255, 255, 0.1)',
}));

const PartsTable = ({
  enableDarkTheme,
  data,
  healthData,
  selectedPart,
  selectedAsset,
  metadata,
}: {
  enableDarkTheme: boolean;
  data: any[];
  healthData: any;
  selectedPart: string[];
  selectedAsset: string[];
  metadata: any[];
}) => {
  const palette = enableDarkTheme
    ? clientThemePalette[CLIENT_THEME].dark
    : clientThemePalette[CLIENT_THEME].light;
  const controlBackground = enableDarkTheme
    ? palette.paperBackground
    : palette.boxBackground;
  const tableBackground =
    CLIENT_THEME === 'yokohama' ? palette.paperBackground : '#1C3459';
  const tableHeaderBackground =
    CLIENT_THEME === 'yokohama' ? palette.sectionBackground : '#1C3459';
  const usageBadgeBackground = palette.primary;
  const { t } = useTranslation();
  const [sortType, setSortType] = useState('Historical Usage');
  const [searchTerm, setSearchTerm] = useState('');
  const [eolThreshold, setEolThreshold] = useState('1M');
  const [failureModeFilter, setFailureModeFilter] = useState('Time-based');

  const handleChange = (event: any) => {
    const newValue = event.target.value;
    setSearchTerm(newValue);
  };

  const formatCategoryText = (text: string) => {
    if (!text) return '';
    return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
  };

  const replacementData = data.filter((item) => {
    const subCategory = item.sub_component_categories.replace(/^\[|\]$/g, '');
    const combinedCategory = `${item.components_category} - ${subCategory}`;

    return (
      item.actions_performed === 'REPLACE' &&
      selectedAsset.includes(item.asset_id) &&
      selectedPart.includes(combinedCategory)
    );
  });

  const categoryData: any[] = getUniqueComponentCounts(replacementData);

  const filteredHealthData = [...healthData].filter((item) => {
    const combinedCategory = `${item.component} - ${item.sub_component}`;

    return (
      selectedAsset.includes(item.bumper_id) &&
      selectedPart.includes(combinedCategory)
    );
  });

  function getEolValue(eol: string) {
    if (eol === '1M') {
      return 30;
    }
    if (eol === '3M') {
      return 90;
    }
    if (eol === '6M') {
      return 180;
    }
    if (eol === '1Y') {
      return 366;
    }
    return 30;
  }

  function getSortKey(key: string) {
    if (key === 'Recommended Stock Level') {
      return 'eolThreshold';
    }
    return 'count';
  }

  const thresholdValue = getEolValue(eolThreshold);

  const processedData = [...categoryData]
    .map((item) => {
      const [primaryComponent, subComponent] = item.category.split(' - ');
      const itemHealthAppearance = [...filteredHealthData].filter(
        (hd) =>
          hd.component === primaryComponent &&
          hd.sub_component === subComponent &&
          hd.days_to_eol <= thresholdValue
      ).length;

      const failureMode = metadata.filter(
        (item) =>
          item.component === primaryComponent &&
          item.sub_component === subComponent
      );

      return {
        ...item,
        eolThreshold: itemHealthAppearance,
        category: formatCategoryText(item.category),
        failureMode: failureMode[0]?.failure_mode ?? '--',
      };
    })
    .filter((item) =>
      searchTerm
        ? item.category.toLowerCase().includes(searchTerm.toLowerCase())
        : true
    )
    .filter((row) => {
      if (failureModeFilter === 'All') return true;
      return row.failureMode?.toLowerCase() === failureModeFilter.toLowerCase();
    })
    .sort((a, b) => b[getSortKey(sortType)] - a[getSortKey(sortType)]);

  return (
    <Box
      sx={{
        marginTop: '20px',
        backgroundColor: enableDarkTheme
          ? palette.boxBackground
          : palette.toolBarBackground,
        borderRadius: '5px',
        paddingTop: '10px',
        paddingBottom: '20px',
        marginBottom: '20px',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          padding: '10px',
          marginBottom: '10px',
        }}
      >
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '14px',
              marginTop: '10px',
              marginRight: '10px',
            }}
          >
            {t('parts_stock_recommendations')}
          </Typography>
          <SearchContainer
            elevation={0}
            sx={{
              backgroundColor: controlBackground,
              border: `1px solid ${palette.cardBorderColor}`,
            }}
          >
            <StyledInputBase
              placeholder={t('search_for_a_part')}
              inputProps={{ 'aria-label': 'search for a part' }}
              value={searchTerm}
              onChange={handleChange}
              sx={{
                color: palette.textPrimary,
                '& .MuiInputBase-input::placeholder': {
                  color: palette.textSecondary,
                },
              }}
            />
            <SearchButton
              type="button"
              aria-label="search"
              sx={{ color: palette.textSecondary }}
            >
              <SearchIcon />
            </SearchButton>
          </SearchContainer>
          <Typography
            sx={{
              fontSize: '14px',
              marginTop: '10px',
              marginLeft: '10px',
              marginRight: '10px',
              color: palette.textPrimary,
            }}
          >
            {t('failure_mode')}
          </Typography>
          <Select
            value={failureModeFilter}
            label={t('failure_mode_filter')}
            onChange={(e) => {
              setFailureModeFilter(e.target.value);
            }}
            sx={{
              height: '40px',
              fontSize: '14px',
              marginLeft: '10px',
              marginRight: '20px',
              backgroundColor: controlBackground,
              color: palette.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.textSecondary,
              },
              '& fieldset': {
                borderColor: palette.textPrimary,
              },
              ...(enableDarkTheme && {
                '&.MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: palette.cardBorderColor,
                  },
                },
              }),
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                  color: palette.textPrimary,
                  fontSize: '14px',
                },
              },
            }}
          >
            <MenuItem value="All">{t('all')}</MenuItem>
            <MenuItem value="Random">Random</MenuItem>
            <MenuItem value="Time-based">{t('time_based')}</MenuItem>
            <MenuItem value="--">--</MenuItem>
          </Select>
        </Box>
        <Box
          sx={{
            display: 'flex',
          }}
        >
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '14px',
              marginTop: '8px',
              marginRight: '10px',
            }}
          >
            Threshold
          </Typography>
          <Select
            labelId="parts-recommendation-threshold-days"
            value={eolThreshold}
            onChange={(e: any) => {
              setEolThreshold(e.target.value);
            }}
            sx={{
              height: '40px',
              fontSize: '14px',
              marginLeft: '10px',
              marginRight: '20px',
              backgroundColor: controlBackground,
              color: palette.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.textSecondary,
              },
              '& fieldset': {
                borderColor: palette.textPrimary,
              },
              ...(enableDarkTheme && {
                '&.MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: palette.cardBorderColor,
                  },
                },
              }),
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                  color: palette.textPrimary,
                  fontSize: '14px',
                },
              },
            }}
          >
            {['1M', '3M', '6M', '1Y'].map((type, index) => (
              <MenuItem
                key={index}
                value={type}
                sx={{
                  fontSize: '14px',
                }}
              >
                {type}
              </MenuItem>
            ))}
          </Select>
          <Typography
            sx={{
              color: palette.textPrimary,
              fontSize: '14px',
              marginTop: '8px',
            }}
          >
            {t('sort_by_10')}
          </Typography>
          <Select
            labelId="parts-recommendation-sub-components-sort"
            value={sortType}
            onChange={(e: any) => {
              setSortType(e.target.value);
            }}
            sx={{
              height: '40px',
              fontSize: '14px',
              marginLeft: '10px',
              marginRight: '20px',
              backgroundColor: controlBackground,
              color: palette.textPrimary,
              '& .MuiSelect-icon': {
                color: palette.textSecondary,
              },
              '& fieldset': {
                borderColor: palette.textPrimary,
              },
              ...(enableDarkTheme && {
                '&.MuiOutlinedInput-root': {
                  '& fieldset': {
                    borderColor: palette.cardBorderColor,
                  },
                },
              }),
            }}
            MenuProps={{
              PaperProps: {
                style: {
                  backgroundColor: enableDarkTheme
                    ? palette.menuOptions
                    : palette.boxBackground,
                  color: palette.textPrimary,
                  fontSize: '14px',
                },
              },
            }}
          >
            {['Historical Usage', 'Recommended Stock Level'].map(
              (type, index) => (
                <MenuItem
                  key={index}
                  value={type}
                  sx={{
                    fontSize: '14px',
                  }}
                >
                  {type}
                </MenuItem>
              )
            )}
          </Select>
        </Box>
      </Box>
      <Box sx={{ width: '100%' }}>
        <TableContainer
          sx={{
            color: 'white',
            overflow: 'scroll',
            maxHeight: '500px',
          }}
        >
          <Table
            sx={{
              minWidth: 650,
              borderCollapse: 'collapse',
              '& .MuiTableCell-root': {
                borderBottom: 'none',
              },
              backgroundColor: tableBackground,
            }}
            size="small"
          >
            <TableHead
              sx={{
                backgroundColor: tableHeaderBackground,
              }}
            >
              <TableRow>
                <TableCell sx={{ color: 'white', fontWeight: 500, py: 2 }}>
                  {t('part')}
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 500, py: 2 }}
                  align="center"
                >
                  {t('failure_mode')}
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 500, py: 2 }}
                  align="center"
                >
                  {t('historical_usage')}
                </TableCell>
                <TableCell
                  sx={{ color: 'white', fontWeight: 500, py: 2 }}
                  align="center"
                >
                  {t('recommended_stock_level')}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {processedData.map((row, index) => (
                <TableRow
                  key={index}
                  sx={{
                    '&:not(:last-child)': {
                      borderBottom: `1px solid ${palette.cardBorderColor}`,
                    },
                  }}
                >
                  <TableCell
                    scope="row"
                    sx={{ color: palette.textPrimary, py: 2 }}
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {row.category}
                    </Typography>
                  </TableCell>
                  <TableCell
                    scope="row"
                    sx={{ color: palette.textPrimary, py: 2 }}
                    align="center"
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {row.failureMode}
                    </Typography>
                  </TableCell>
                  <TableCell
                    sx={{ color: palette.textPrimary, py: 2 }}
                    align="center"
                  >
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        bgcolor: usageBadgeBackground,
                        borderRadius: '999px',
                        px: 2,
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body2" sx={{ mr: 1 }}>
                        {row.count}
                      </Typography>
                    </Box>
                  </TableCell>
                  <TableCell
                    scope="row"
                    sx={{ color: palette.textPrimary, py: 2 }}
                    align="center"
                  >
                    <Typography variant="body1" sx={{ fontWeight: 500 }}>
                      {row.failureMode?.toLowerCase() === 'random' &&
                      row.eolThreshold === 0
                        ? '--'
                        : row.eolThreshold}
                    </Typography>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      </Box>
    </Box>
  );
};

export default PartsTable;
